import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api.js';
import { fmtUsd, fmtUsd2, fmtNum, ymdLocal } from '../lib/format.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ReportsSkeleton } from './ReportsSkeleton.jsx';
import { EventsBanner } from './EventsBanner.jsx';
import { DateStepper } from './DateStepper.jsx';
import { ClockAlertsBanner } from './ClockAlertsBanner.jsx';
import { ScorecardCard } from './ScorecardCard.jsx';

// Time on the clock so far, compact (e.g. "2h 10m", "45m").
function sinceLabel(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms > 0)) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins % 60}m`;
}
import { DeltaText } from './DeltaText.jsx';
import { HourlyChart } from './HourlyChart.jsx';
import { PhaseStrip } from './PhaseStrip.jsx';
import { ScheduleGantt } from './ScheduleGantt.jsx';

// ISO weekday number (1=Mon..7=Sun) → short name (used only here).
function dayName(isoDow) {
  return ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][(isoDow || 1) - 1];
}

// variant='daily' (web Daily view, CJ 2026-07-29) reorders to Events → leadSlot
// (Weather + Morning PAR) → today's staffing → hourly forecast → sales scorecard
// → roster, matching the staff-facing top-down flow. Default order (used by This
// Week) is unchanged. leadSlot is a node rendered only in the daily variant.
export function RealtimeDashboard({ location, user, variant, leadSlot = null }) {
  const isManager = user && window.EmpireRoles ? window.EmpireRoles.isManager(user) : false;
  const [dateYmd, setDateYmd] = useState(() => ymdLocal(new Date()));
  const [data, setData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [error, setError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [cogs, setCogs] = useState(null);
  const cogsGood = useRef(null); // session last-good, so a Tally blip never blanks
  const locationKey = locationKeyFromName(location?.name);

  // Scorecard / hourly / phase — Square orders only, fast.
  // Round 22: force=1 bypasses the 20s server cache when called from
  // the user-clicked Refresh button. Auto-refreshes don't pass force.
  const loadRealtime = useCallback(async ({ force = false } = {}) => {
    if (!locationKey) return;
    try {
      const url = `/api/realtime/${locationKey}?date=${dateYmd}${force ? '&force=1' : ''}`;
      const res = await api('GET', url);
      setData(res); setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load daily data');
    } finally {
      setLoading(false);
    }
  }, [locationKey, dateYmd]);

  // Schedule + clock alerts — Square Labor (any past/current/future date).
  const loadSchedule = useCallback(async () => {
    if (!locationKey) return;
    try {
      const res = await api('GET', `/api/schedule/${locationKey}?date=${dateYmd}`);
      setScheduleData(res); setScheduleError(null);
    } catch (e) {
      setScheduleError(e.message || 'Failed to load schedule');
    } finally {
      setScheduleLoading(false);
    }
  }, [locationKey, dateYmd]);

  // Theoretical COGS for this store from Tally (server-side proxy). Only
  // meaningful for today; keep last-good so a non-200 never blanks or zeroes.
  const loadCogs = useCallback(async () => {
    if (!location?.name) return;
    const today = dateYmd === ymdLocal(new Date());
    if (!today) { setCogs(null); return; }
    try {
      const r = await api('GET', `/api/operations/cogs-today?location=${encodeURIComponent(location.name)}`);
      if (r && r.available !== false) { cogsGood.current = r; setCogs(r); }
      else if (cogsGood.current) setCogs({ ...cogsGood.current, stale: true });
      else setCogs(r || { available: false });
    } catch {
      if (cogsGood.current) setCogs({ ...cogsGood.current, stale: true });
    }
  }, [location?.name, dateYmd]);

  const reload = useCallback(({ force = false } = {}) => {
    setLoading(true); setScheduleLoading(true);
    setData(null); setScheduleData(null);
    loadRealtime({ force }); loadSchedule(); loadCogs();
  }, [loadRealtime, loadSchedule, loadCogs]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-refresh only when viewing today. Round 5 tightened this from
  // 5 min → 60 s so the dashboard never lags real-world activity by
  // more than ~80 s (server cache 20 s + poll interval 60 s). Server-side
  // realtimeCache prevents this from melting Square — multiple browsers
  // refreshing at the same time still hit the same cached payload.
  useEffect(() => {
    if (dateYmd !== ymdLocal(new Date())) return;
    const t = setInterval(() => { loadRealtime(); loadSchedule(); loadCogs(); }, 60 * 1000);
    return () => clearInterval(t);
  }, [dateYmd, loadRealtime, loadSchedule, loadCogs]);

  if (!locationKey) return <div className="card text-sm text-gray-500">Pick a location to see daily data.</div>;
  if (loading && !data) return <ReportsSkeleton label={`Loading ${dateYmd} · ${location.name}…`} />;
  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-sm font-semibold text-red-700 mb-1">Daily data unavailable</div>
    <div className="text-xs text-gray-600">{error}</div>
    <button className="btn-secondary mt-3" onClick={reload}>Retry</button>
  </div>;
  if (!data) return null;

  const { meta, scorecard, hourlyChart, decisionGuide } = data;
  const isToday = !!meta.isToday;
  const isFuture = (meta.dateYmd || dateYmd) > ymdLocal(new Date());
  const lastUpdated = meta.lastUpdated
    ? new Date(meta.lastUpdated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : null;
  const subjLabel = isToday ? 'today' : new Date(dateYmd + 'T12:00:00')
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const compareLabel = `vs ${dayName(meta.isoWeekInfo.dayOfWeek)} a week ago`;

  // Theoretical COGS (Tally) + est. net margin for this store, today only.
  const cogsAvail = !!(cogs && cogs.available !== false && cogs.cogs != null);
  const cogsAsOf = cogs?.as_of
    ? new Date(cogs.as_of).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
    : null;
  const cogsFlag = !cogsAvail ? 'gray'
    : cogs.food_cost_pct < 30 ? 'green' : cogs.food_cost_pct < 35 ? 'amber' : 'red';
  // Who's on the clock right now at this store (names behind the count).
  const onClockPeople = scorecard?.laborPct?.onClockPeople || [];

  // Schedule from the new endpoint (any date). Cards/banner read from here.
  const sched = scheduleData?.schedule || null;
  const scheduleStatusBadge =
    isFuture ? 'planning' :
    isToday ? 'live' : 'past';

  const eventsEl = (
    <EventsBanner locationId={location?.id} locationName={location?.name} dateYmd={dateYmd} />
  );
  const chromeEl = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <DateStepper dateYmd={meta.dateYmd || dateYmd} isToday={isToday} onChange={setDateYmd} />
      <div style={{ fontSize: 12, color: '#757575', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: isToday ? '#2e7d32' : '#9e9e9e', marginRight: 6 }}/>
          {isToday
            ? (lastUpdated ? `Live · data as of ${lastUpdated}` : 'Live')
            : isFuture ? 'Future · planning view' : 'Past day'}
          {meta.cached && ' · cached'}
        </span>
        <button className="btn-secondary" onClick={() => reload({ force: true })}>↻ Refresh</button>
      </div>
    </div>
  );
  const alertsEl = <ClockAlertsBanner alerts={sched?.clockAlerts} />;

  const scorecardEl = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        {isManager && (
          <ScorecardCard label={isToday ? 'Revenue today' : isFuture ? `Revenue (forecast) ${subjLabel}` : `Revenue ${subjLabel}`}
            value={isFuture ? '—' : fmtUsd(scorecard.revenue.value)}
            sub={isFuture ? 'No actuals — future date' : <><DeltaText value={scorecard.revenue.deltaPct} suffix={compareLabel} /> · Square net, after discounts</>}
            flag={isFuture ? 'gray' : scorecard.revenue.flag} />
        )}
        <ScorecardCard label="Transactions"
          value={isFuture ? '—' : fmtNum(scorecard.transactions.value)}
          sub={isFuture ? 'No actuals — future date' : <DeltaText value={scorecard.transactions.deltaPct} suffix={compareLabel} />}
          flag={isFuture ? 'gray' : scorecard.transactions.flag} />
        {isManager && (
          <ScorecardCard label="Avg ticket"
            value={isFuture ? '—' : fmtUsd2(scorecard.avgTicket.value)}
            sub={null}
            flag={isFuture ? 'gray' : scorecard.avgTicket.flag} />
        )}
        {isManager && (
          <ScorecardCard label="Labor today"
            value={scorecard.laborPct.value != null ? `${scorecard.laborPct.value.toFixed(1)}%` : '—'}
            sub={scorecard.laborPct.note
              ? scorecard.laborPct.note
              : (scorecard.laborPct.laborCostToday != null
                  ? `$${scorecard.laborPct.laborCostToday.toFixed(0)} · ${scorecard.laborPct.laborHoursToday?.toFixed(1) || '0.0'} hrs`
                  : `Target: ${meta.thresholds.LABOR_TARGET_LOW}–${meta.thresholds.LABOR_TARGET_HIGH}%`)}
            flag={scorecard.laborPct.flag} />
        )}
        {isManager && isToday && (
          <ScorecardCard label="Labor right now"
            value={scorecard.laborPct.currentHourPct != null ? `${scorecard.laborPct.currentHourPct.toFixed(1)}%` : '—'}
            sub={scorecard.laborPct.burnRatePerHour > 0
              ? `$${scorecard.laborPct.burnRatePerHour.toFixed(0)}/hr · ${scorecard.laborPct.onClockCount || 0} on clock`
              : 'No one clocked in'}
            flag={scorecard.laborPct.currentHourFlag || 'gray'} />
        )}
        {/* Managers see COGS only — net margin (profitability) is owner-only. */}
        {isManager && isToday && (
          <ScorecardCard label="Theoretical COGS"
            value={cogsAvail ? `${cogs.food_cost_pct.toFixed(1)}%` : '—'}
            sub={cogsAvail
              // Headline % is over costed sales; show the same real cogs$ over ALL
              // sales too so the % ties to the $ (CJ 2026-07-18).
              ? `of costed sales (Tally, pre-discount) · ${fmtUsd(cogs.cogs)}${cogs.revenue > 0 ? ` · ${(cogs.cogs / cogs.revenue * 100).toFixed(1)}% of all sales` : ''}${cogs.costed_pct != null ? ` · ${Math.round(cogs.costed_pct)}% costed` : ''}${cogsAsOf ? ` · as of ${cogsAsOf} ET${cogs.stale ? ' (last good)' : ''}` : ''}`
              : (cogs?.costed_pct === 0 ? 'No recipes costed yet' : 'No number yet')}
            flag={cogsFlag} />
        )}
        {sched?.staffScheduled != null && sched.staffScheduled > 0 && (
          <ScorecardCard label={isToday ? 'On clock' : isFuture ? 'Scheduled' : 'Worked'}
            value={isToday ? `${sched.staffOnClock ?? 0} / ${sched.staffScheduled}` : `${sched.staffScheduled}`}
            sub={null}
            flag={isToday
              ? (sched.staffOnClock === 0 && sched.staffScheduled > 0 ? 'red'
                : (sched.clockAlerts?.length > 0 ? 'yellow' : 'green'))
              : 'gray'} />
        )}
      </div>
  );

  const staffingEl = (isManager && isToday) ? (
    <div className="card">
      <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 }}>
        On the clock {onClockPeople.length > 0 ? `(${onClockPeople.length})` : ''}
      </div>
      {onClockPeople.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>No one clocked in</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {onClockPeople.map((p, i) => (
            <span key={i} style={{ fontSize: 12, color: '#212121', background: '#f4f6f5',
                                    borderRadius: 12, padding: '3px 10px', fontWeight: 500 }}>
              {p.name}{sinceLabel(p.since) ? <span style={{ color: '#9e9e9e', fontWeight: 400 }}> · {sinceLabel(p.since)}</span> : null}
            </span>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const hourlyEl = !isFuture ? <HourlyChart chart={hourlyChart} forecastSource={meta.forecastSource} /> : null;
  const phaseEl = !isFuture ? <PhaseStrip guide={decisionGuide} /> : null;
  const ganttEl = (
    <ScheduleGantt schedule={sched} isToday={isToday}
      statusBadge={scheduleStatusBadge}
      loading={scheduleLoading}
      error={scheduleError} />
  );

  // Daily (staff-facing): Events → Weather + Morning PAR (leadSlot) → staffing →
  // hourly → sales scorecard → roster. Default (This Week): original order.
  if (variant === 'daily') {
    return (
      <div className="space-y-3">
        {eventsEl}
        {leadSlot}
        {chromeEl}
        {alertsEl}
        {staffingEl}
        {hourlyEl}
        {scorecardEl}
        {phaseEl}
        {ganttEl}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {eventsEl}
      {chromeEl}
      {alertsEl}
      {scorecardEl}
      {staffingEl}
      {hourlyEl}
      {phaseEl}
      {ganttEl}
    </div>
  );
}
